import { Injectable, inject } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';

/**
 * SocketService — wrapper genérico sobre Socket.IO.
 *
 * Seguridad (v3.5):
 *  - Manda `auth.token` (vía `localStorage.getItem('accessToken')`) en el
 *    handshake. El backend (`AuthenticatedWsAdapter`) rechaza conexiones
 *    sin JWT válido. Esto cierra el hueco donde el WS aceptaba clientes
 *    anónimos.
 *  - Si la conexión falla con `unauthorized:*`, emite un Subject
 *    `authErrors$` para que la app pueda reaccionar (signOut, reintento).
 *  - Cachea el `connect_error` para que la consola no se inunde en dev.
 *
 *  Por qué lazy-init: el patrón original ya creaba el socket en el
 *  constructor. Sólo cambiamos el `auth` y los handlers.
 */
@Injectable({
    providedIn: 'root',
})
export class SocketService {
    private readonly authService = inject(AuthService);

    config: any = {
        url: environment.wssBase,
        options: {
            reconnection: environment.production,
            reconnectionAttempts: environment.production ? 5 : 0,
            reconnectionDelay: 10000,
            reconnectionDelayMax: 50000,
            timeout: 20000,
            autoConnect: false,
            upgrade: true,
            transports: ['websocket'],
            auth: this.tokenForSocket(),
        },
    };
    private socket: Socket;

    /** Subject de errores de auth para que la app reaccione (login, retry). */
    private readonly authErrorSubject = new Subject<string>();
    public readonly authErrors$ = this.authErrorSubject.asObservable();

    constructor() {
        this.socket = io(this.config.url, this.config.options);

        // Centralizar manejo de errores para que la app entera pueda reaccionar.
        this.socket.on('connect_error', (err: any) => {
            const msg: string = String(err?.message ?? err);
            console.warn('[SocketService] connect_error:', msg);
            if (msg.toLowerCase().includes('unauthorized')) {
                this.authErrorSubject.next(msg);
                // Si el token venció o es inválido, forzar logout.
                if (msg.includes('expired') || msg.includes('invalid-signature')) {
                    try {
                        this.authService.signOut();
                    } catch {
                        /* noop si no hay sesión activa */
                    }
                }
            }
        });

        // Errores de runtime del servidor (rate limit, payload too large).
        this.socket.on('ws:error', (err: any) => {
            console.warn('[SocketService] ws:error', err);
        });
    }

    private tokenForSocket(): { token: string } {
        // `AuthService.accessToken` setter/getter persiste en localStorage.
        try {
            const token = this.authService.accessToken ?? '';
            return { token };
        } catch {
            return { token: '' };
        }
    }

    /** Permite refrescar el token entre reconexiones (rotación, re-login). */
    refreshAuthToken(): void {
        this.socket.auth = this.tokenForSocket();
    }

    private ensureConnection(): void {
        if (!this.socket.connected && !this.socket.active) {
            this.connect();
        }
    }

    // Compatibilidad: el chat legacy espera mandar `{message, chatId}`.
    sendMessage(event: string, message: any, chatId?: any): void {
        this.ensureConnection();
        if (typeof chatId !== 'undefined') {
            this.socket.emit(event, { message, chatId });
            return;
        }
        this.socket.emit(event, message);
    }

    emit(event: string, payload: any): void {
        this.ensureConnection();
        this.socket.emit(event, payload);
    }

    onMessage(event: string): Observable<any> {
        return new Observable((observer) => {
            this.ensureConnection();
            this.socket.on(event, (data) => {
                observer.next(data);
            });
            return () => {
                this.socket.off(event);
            };
        });
    }

    disconnect(): void {
        if (this.socket) {
            this.socket = this.socket.disconnect();
        }
    }

    connect(): void {
        if (!this.socket.connected) {
            this.socket = this.socket.connect();
        }
    }
}
